import type { Order, OrderItem, Role } from "../shared/order-domain";
import type { OrderWrite } from "../shared/order-schema";

const API = "/api";

interface ApiErrorPayload {
  error?: string;
}

interface SessionResponse {
  role: Role;
  label: string;
}

interface UploadResponse {
  key: string;
  name: string;
}

interface CountResponse {
  ok: true;
  count: number;
}

interface TransferResponse extends CountResponse {
  total: number;
  transferred_at: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(code);
  }
}

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${API}${path}`, { credentials: "include", ...init });
}

async function responseError(response: Response): Promise<ApiError> {
  const payload = await response.json().catch(() => ({} as ApiErrorPayload));
  return new ApiError(response.status, payload.error || "request_failed");
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiFetch(path, init);
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<T>;
}

function jsonRequest(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

export function loadOrders(): Promise<Order[]> {
  return requestJson<Order[]>("/orders");
}

export function saveOrder(input: OrderWrite, id?: number): Promise<Order> {
  return requestJson<Order>(id ? `/orders/${id}` : "/orders", jsonRequest(id ? "PUT" : "POST", input));
}

export async function removeOrder(id: number): Promise<void> {
  await requestJson(`/orders/${id}`, { method: "DELETE" });
}

export function setItemPrinted(id: number, isPrinted: boolean): Promise<OrderItem> {
  return requestJson<OrderItem>(`/items/${id}/printed`, jsonRequest("PATCH", { is_printed: isPrinted }));
}

export function setOrderPrinted(id: number, isPrinted: boolean): Promise<Order> {
  return requestJson<Order>(`/orders/${id}/printed`, jsonRequest("PATCH", { is_printed: isPrinted }));
}

export function acknowledgeAllItems(): Promise<CountResponse> {
  return requestJson<CountResponse>("/items/acknowledge-all", { method: "POST" });
}

export function updateItemFile(
  id: number,
  fileKey: string,
  fileName: string,
): Promise<OrderItem> {
  return requestJson<OrderItem>(
    `/items/${id}/file`,
    jsonRequest("PATCH", { file_key: fileKey, file_name: fileName }),
  );
}

export async function uploadFile(file: File): Promise<UploadResponse> {
  const body = new FormData();
  body.append("file", file);
  return requestJson<UploadResponse>("/files", { method: "POST", body });
}

export async function discardFile(fileKey: string): Promise<void> {
  await requestJson(`/files/${fileKey.split("/").map(encodeURIComponent).join("/")}`, {
    method: "DELETE",
  });
}

export function currentSession(): Promise<SessionResponse> {
  return requestJson<SessionResponse>("/auth/me");
}

export function login(code: string): Promise<SessionResponse> {
  return requestJson<SessionResponse>("/auth/login", jsonRequest("POST", { code }));
}

export async function logout(): Promise<void> {
  await requestJson("/auth/logout", { method: "POST" });
}

export function transferAll(): Promise<TransferResponse> {
  return requestJson<TransferResponse>("/transfer-all", { method: "POST" });
}

export function undoLatestTransfer(): Promise<TransferResponse> {
  return requestJson<TransferResponse>("/transfer-all", { method: "DELETE" });
}
